/**
 * Provider normalization for subscriptions.
 *
 * The DB `subscriptions.purchase_platform` column may store legacy or
 * abbreviated values ("google" vs "google_play"). At the application level we
 * normalize to a small closed set so UI and access logic don't have to
 * duplicate the mapping.
 */

export type SubscriptionProvider = "stripe" | "apple" | "google_play" | null;

export type BillingManagementType =
  | "stripe"
  | "apple"
  | "google_play"
  | "institutional"
  | "trial"
  | "free"
  | "admin"
  | null;

/**
 * Normalize a raw purchase_platform value to the application-level provider.
 * Unknown / empty values collapse to `null`.
 */
export function normalizeProvider(raw: string | null | undefined): SubscriptionProvider {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (v === "stripe") return "stripe";
  if (v === "apple") return "apple";
  if (v === "google" || v === "google_play" || v === "googleplay") return "google_play";
  return null;
}

export interface RawSubscriptionRow {
  status: string | null;
  current_period_end: string | null;
  purchase_platform?: string | null;
}

export interface DerivedSubscriptionState {
  /** True if any row is currently active/trialing and not expired. */
  hasActiveSubscription: boolean;
  hasActiveStripeSubscription: boolean;
  hasActiveAppleSubscription: boolean;
  hasActiveGooglePlaySubscription: boolean;
  /** True when the user holds active paid subs from >1 distinct provider. */
  hasMultiplePaidSubscriptions: boolean;
  /**
   * Preferred provider for a currently-active subscription (paid providers
   * only). Stripe wins ties for legacy safety. `null` when no paid provider
   * is active.
   */
  subscriptionProvider: SubscriptionProvider;
}

function isRowActive(row: RawSubscriptionRow): boolean {
  const status = (row.status ?? "").toLowerCase();
  if (status !== "active" && status !== "trialing") return false;
  if (!row.current_period_end) return true;
  const end = new Date(row.current_period_end).getTime();
  return Number.isFinite(end) ? end > Date.now() : true;
}

export function deriveSubscriptionState(
  rows: RawSubscriptionRow[] | null | undefined,
): DerivedSubscriptionState {
  const list = Array.isArray(rows) ? rows : [];
  const activeProviders = new Set<Exclude<SubscriptionProvider, null>>();

  for (const row of list) {
    if (!isRowActive(row)) continue;
    // Legacy rows may have null purchase_platform — treat as Stripe (the
    // only historical provider) to preserve existing behavior.
    const provider = normalizeProvider(row.purchase_platform) ?? "stripe";
    activeProviders.add(provider);
  }

  const hasActiveStripeSubscription = activeProviders.has("stripe");
  const hasActiveAppleSubscription = activeProviders.has("apple");
  const hasActiveGooglePlaySubscription = activeProviders.has("google_play");
  const hasActiveSubscription = activeProviders.size > 0;
  const hasMultiplePaidSubscriptions = activeProviders.size > 1;

  let subscriptionProvider: SubscriptionProvider = null;
  if (hasActiveStripeSubscription) subscriptionProvider = "stripe";
  else if (hasActiveAppleSubscription) subscriptionProvider = "apple";
  else if (hasActiveGooglePlaySubscription) subscriptionProvider = "google_play";

  return {
    hasActiveSubscription,
    hasActiveStripeSubscription,
    hasActiveAppleSubscription,
    hasActiveGooglePlaySubscription,
    hasMultiplePaidSubscriptions,
    subscriptionProvider,
  };
}

export interface BillingManagementInput {
  isAdmin?: boolean;
  hasFreeAccess: boolean;
  hasCohortAccess: boolean;
  isOnTrial: boolean;
  subscriptionProvider: SubscriptionProvider;
}

/**
 * Which billing surface (if any) should be exposed to the user in Settings.
 * Priority: active paid provider first (users need to manage recurring
 * charges), then institutional / trial / free / admin informational states.
 */
export function resolveBillingManagementType(
  input: BillingManagementInput,
): BillingManagementType {
  if (input.subscriptionProvider === "stripe") return "stripe";
  if (input.subscriptionProvider === "apple") return "apple";
  if (input.subscriptionProvider === "google_play") return "google_play";
  if (input.hasCohortAccess) return "institutional";
  if (input.isOnTrial) return "trial";
  if (input.hasFreeAccess) return "free";
  if (input.isAdmin) return "admin";
  return null;
}
